// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AppIcon, Button, IconButton, useToast } from '@loa/ui-components';
import { proposalApi } from '../../../../../api/proposalApi';
import { fundsApi } from '../../../../../api/fundsApi';
import { FundSearchSelect } from '../../../../funds/FundSearchSelect';
import { TiptapEditor } from '../../../../invitation/TiptapEditor';
import type {
  BreakthroughProposalDetails,
  Proposal,
  ProposalSection,
  RichTextSectionContent,
} from '../../../../../types/proposal';

export type SectionFundsProps = Readonly<{
  proposalId: string;
  proposal: Proposal;
  section: ProposalSection;
  onSaved: (updated: ProposalSection) => void;
  onUpdateDetails: (partial: Partial<BreakthroughProposalDetails>) => Promise<void>;
  /**
   * Renders the "Client view" preview (BR-P5, PRD §5 / RQ-BR8): the fund-key
   * list becomes a plain read-only list (no add/remove row) and the Tiptap
   * commentary editor is disabled with both Save buttons hidden. Defaults to
   * false (advisor editable builder).
   */
  readOnly?: boolean;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

function firstBlockHtml(section: ProposalSection): string {
  const content = section.content as RichTextSectionContent | null;
  return content?.blocks?.[0]?.html ?? '';
}

function readDetails(proposal: Proposal): BreakthroughProposalDetails {
  return (proposal.details as BreakthroughProposalDetails | null) ?? {};
}

/**
 * BR_FUNDS (PRD §4 row 11) — a v1 recommended-funds reference list
 * (`fund_key` strings, persisted on the proposal's detail row; a full
 * funds-service picker is a later enhancement) plus the advisor's authored
 * fund deep-dive commentary (rich text, saved onto the section content like
 * the other authored sections).
 */
export function SectionFunds({
  proposalId,
  proposal,
  section,
  onSaved,
  onUpdateDetails,
  readOnly = false,
}: SectionFundsProps) {
  const { showToast } = useToast();

  const [fundKeys, setFundKeys] = useState<string[]>(readDetails(proposal).recommendedFundKeys ?? []);
  const [nameByKey, setNameByKey] = useState<Record<string, string>>({});
  const [keysDirty, setKeysDirty] = useState(false);
  const [savingKeys, setSavingKeys] = useState(false);

  const [html, setHtml] = useState(() => firstBlockHtml(section));
  const [htmlDirty, setHtmlDirty] = useState(false);
  const [savingHtml, setSavingHtml] = useState(false);

  // Resolve friendly fund names for any keys we don't already have a label
  // for (best-effort — falls back to the raw key when funds-service is off).
  useEffect(() => {
    const missing = fundKeys.filter((k) => !(k in nameByKey));
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(
      missing.map((key) =>
        fundsApi
          .get(key)
          .then((fund) => [key, fund.fundName] as const)
          .catch(() => [key, key] as const),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      setNameByKey((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });
    return () => {
      cancelled = true;
    };
  }, [fundKeys, nameByKey]);

  const addKey = useCallback((key: string, name?: string) => {
    const trimmed = key.trim();
    if (!trimmed) return;
    setFundKeys((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
    if (name) setNameByKey((prev) => ({ ...prev, [trimmed]: name }));
    setKeysDirty(true);
  }, []);

  const handleRemoveFund = useCallback((key: string) => {
    setFundKeys((prev) => prev.filter((k) => k !== key));
    setKeysDirty(true);
  }, []);

  const handleSaveFunds = useCallback(async () => {
    if (savingKeys) return;
    setSavingKeys(true);
    try {
      await onUpdateDetails({ recommendedFundKeys: fundKeys });
      setKeysDirty(false);
      showToast('Recommended funds saved', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to save the recommended funds'), 'error');
    } finally {
      setSavingKeys(false);
    }
  }, [savingKeys, onUpdateDetails, fundKeys, showToast]);

  const handleSaveHtml = useCallback(async () => {
    if (savingHtml) return;
    setSavingHtml(true);
    try {
      const existingBlock = (section.content as RichTextSectionContent | null)?.blocks?.[0];
      const updated = await proposalApi.updateSection(proposalId, section.sectionKey, {
        content: { blocks: [{ title: existingBlock?.title, html }] },
      });
      setHtmlDirty(false);
      onSaved(updated);
      showToast('Fund Deep-Dives saved', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to save Fund Deep-Dives'), 'error');
    } finally {
      setSavingHtml(false);
    }
  }, [savingHtml, section, proposalId, html, onSaved, showToast]);

  return (
    <div className="pension-builder__section" data-testid="section-br-funds">
      {readOnly ? (
        fundKeys.length > 0 && (
          <ul className="pension-builder__bullets" data-testid="section-br-funds-keys">
            {fundKeys.map((key) => (
              <li key={key} data-testid={`section-br-funds-key-row-${key}`}>
                {nameByKey[key] ?? key}
              </li>
            ))}
          </ul>
        )
      ) : (
        <>
          <div className="pension-builder__bullets" data-testid="section-br-funds-keys">
            {fundKeys.map((key) => (
              <div
                key={key}
                className="pension-builder__bullet-row"
                data-testid={`section-br-funds-key-row-${key}`}
              >
                <span className="pension-builder__fact-value">{nameByKey[key] ?? key}</span>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveFund(key)}
                  icon={<AppIcon name="trash" />}
                  aria-label={`Remove ${nameByKey[key] ?? key}`}
                  data-testid={`section-br-funds-remove-${key}`}
                />
              </div>
            ))}
            <FundSearchSelect
              onSelect={(fund) => addKey(fund.fundKey, fund.fundName)}
              onManualSelect={(text) => addKey(text)}
              placeholder="Search funds to recommend…"
              testId="section-br-funds-search"
            />
          </div>
          <div className="pension-builder__section-footer">
            <Button
              variant="filled"
              onClick={handleSaveFunds}
              disabled={savingKeys || !keysDirty}
              loading={savingKeys}
              data-testid="section-br-funds-save-keys-btn"
            >
              Save funds
            </Button>
          </div>
        </>
      )}

      <TiptapEditor
        key={section.sectionKey}
        content={html}
        onChange={(next) => {
          setHtml(next);
          setHtmlDirty(true);
        }}
        disabled={readOnly}
        hideToolbar={readOnly}
        minHeight="220px"
        maxHeight="420px"
        ariaLabel="Fund Deep-Dives content"
      />
      {!readOnly && (
        <div className="pension-builder__section-footer">
          <Button
            variant="filled"
            onClick={handleSaveHtml}
            disabled={savingHtml || !htmlDirty}
            loading={savingHtml}
            data-testid="section-br-funds-save-html-btn"
          >
            Save commentary
          </Button>
        </div>
      )}
    </div>
  );
}

export default SectionFunds;
